// DonutLabelingSimilarPanel.jsx — Task 2.4
//
// Shows "유사 GT" matches for the current sample based on car_name and VIN suffix.
// Polls /api/donut/labeling/similar (debounced) whenever car_name or vin changes.
//
// Props:
//   similar : { by_car_name: SimilarItem[], by_vin_suffix: SimilarItem[] }
//   hasCriteria : bool  — true if car_name or vin is non-empty
//   onCopyField : (field: string, value: string) => void

import React from "react";

// Core fields to show in each row (compact display)
const CORE_FIELDS = ["plate_no", "vin", "owner_name", "year"];

function truncate(val, max = 22) {
  if (!val) return null;
  const s = String(val);
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function SimilarItem({ item, onCopyField }) {
  const gt = item.gt || {};
  const displayFields = CORE_FIELDS.filter((f) => gt[f]);

  return (
    <div
      style={{
        padding: "6px 8px",
        borderBottom: "1px solid #f3f4f6",
        fontSize: 11,
      }}
    >
      <div
        style={{
          fontFamily: "monospace",
          color: "#374151",
          fontSize: 11,
          marginBottom: 3,
          fontWeight: 600,
        }}
      >
        {item.id}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 8px" }}>
        {displayFields.map((f) => (
          <button
            key={f}
            title={`${f}: ${gt[f]} → 현재 입력란에 복사`}
            onClick={() => onCopyField(f, gt[f])}
            style={{
              fontSize: 10,
              padding: "1px 6px",
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              color: "#374151",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: "#9ca3af", marginRight: 2 }}>{f}</span>
            {truncate(gt[f], 18)}
          </button>
        ))}
        {displayFields.length === 0 && (
          <span style={{ color: "#9ca3af", fontSize: 10 }}>필드 없음</span>
        )}
      </div>
    </div>
  );
}

function SectionList({ title, items, onCopyField }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div
        style={{
          padding: "5px 8px",
          background: "#f3f4f6",
          fontSize: 11,
          fontWeight: 600,
          color: "#6b7280",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {title} ({items.length}건)
      </div>
      {items.map((item, i) => (
        <SimilarItem key={item.id || i} item={item} onCopyField={onCopyField} />
      ))}
    </div>
  );
}

export default function DonutLabelingSimilarPanel({
  similar,
  hasCriteria,
  onCopyField,
}) {
  const byCar = similar?.by_car_name || [];
  const byVin = similar?.by_vin_suffix || [];
  const total = byCar.length + byVin.length;

  let body;
  if (!hasCriteria) {
    body = (
      <div
        style={{
          padding: "10px 12px",
          color: "#9ca3af",
          fontSize: 11,
        }}
      >
        참고할 유사 GT 가 없습니다
      </div>
    );
  } else if (total === 0) {
    body = (
      <div
        style={{
          padding: "10px 12px",
          color: "#9ca3af",
          fontSize: 11,
        }}
      >
        매칭 없음
      </div>
    );
  } else {
    body = (
      <>
        <SectionList
          title="같은 차종"
          items={byCar}
          onCopyField={onCopyField}
        />
        <SectionList
          title="같은 VIN suffix"
          items={byVin}
          onCopyField={onCopyField}
        />
      </>
    );
  }

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        marginTop: 12,
        overflow: "hidden",
        fontSize: 12,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: "#f9fafb",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <span style={{ fontWeight: 600, color: "#111", fontSize: 12 }}>
          유사 GT (참고)
        </span>
        {total > 0 && (
          <span
            style={{
              fontSize: 11,
              padding: "1px 6px",
              borderRadius: 4,
              background: "#dbeafe",
              color: "#1d4ed8",
            }}
          >
            {total}건
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            fontSize: 10,
            color: "#9ca3af",
          }}
        >
          클릭 → 필드 복사
        </span>
      </div>

      {body}
    </div>
  );
}
